import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Description from '@material-ui/icons/Description';

import { logisticsApi } from 'api/logistics';
import { opsVisibilityApi as api } from 'api/opsVisibility';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

import { FrozenManifestModal } from './FrozenManifestModal';
import { OOBManifestModal } from './OOBManifestModal';
import { ShipmentStatusDetailView } from './shipmentStatusDetailView';

const startOfWeekDate = dayjs().startOf('week').format('YYYY-MM-DD');
const endOfWeekDate = dayjs().endOf('week').format('YYYY-MM-DD');

function useInterval(callback, delay) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

function LinkToDetailView({ initialValue, row, showDetailViewModal }) {
    return (
        <div
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <Button
                style={{ color: 'blue', textDecoration: 'underline' }}
                onClick={async () => {
                    showDetailViewModal(row);
                }}
            >
                <Description />
            </Button>
        </div>
    );
}

function OOBManifestButton({ loading, onOOBManifestClick, row }) {
    return (
        <div
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => onOOBManifestClick(row)}
                disabled={loading}
            >
                {loading ? 'Loading...' : 'OOB Manifest'}
            </Button>
        </div>
    );
}

function FrozenManifestButton({ loading, onFrozenManifestClick, row }) {
    return (
        <div
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <Button
                variant="contained"
                style={{ backgroundColor: '#3b82f6', color: 'white' }}
                size="small"
                onClick={() => onFrozenManifestClick(row)}
                disabled={loading}
            >
                {loading ? 'Loading...' : 'Frozen Manifest'}
            </Button>
        </div>
    );
}

function FilterOptions({
    deliveryType,
    endDate,
    orderSource,
    setDeliveryType,
    setEndDate,
    setOrderSource,
    setStartDate,
    startDate,
}) {
    const [orderSources, setOrderSources] = useState([]);
    const deliveryTypes = ['dbi', 'carrier'];

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
    };

    const getOrderSources = async () => {
        const res = await api.getOrderSources();
        if (!res.status) {
            return;
        }

        setOrderSources(res.data.rows);
    };

    const handleOrderSourceChange = (event) => {
        const {
            target: { value },
        } = event;
        setOrderSource(typeof value === 'string' ? value.split(',') : value);
    };

    useEffect(() => {
        getOrderSources();
    }, []);

    return (
        <div>
            <Grid item xs={12}>
                <TextField
                    id="startDate"
                    label="Start Date"
                    type="date"
                    value={startDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={handleStartDateChange}
                    style={{ marginRight: '50px' }}
                />

                <TextField
                    id="endDate"
                    label="End Date"
                    type="date"
                    value={endDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={handleEndDateChange}
                    style={{ marginRight: '50px' }}
                />
            </Grid>
            <Grid item xs={12} style={{ padding: '20px' }}>
                <FormControl sx={{ m: 1, minWidth: 500 }}>
                    <InputLabel shrink>Order Source</InputLabel>
                    <Select
                        multiple
                        onChange={handleOrderSourceChange}
                        value={orderSource}
                        renderValue={(selected) => selected.join(', ')}
                    >
                        {orderSources.map((item) => (
                            <MenuItem key={item} value={item}>
                                <Checkbox checked={orderSource.indexOf(item) > -1} />
                                <ListItemText primary={item} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} style={{ padding: '10px' }}>
                <InputLabel style={{ padding: '10px' }}>Delivery Type</InputLabel>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="delivery_type_dbi"
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setDeliveryType(event.target.value);
                                } else {
                                    setDeliveryType(null);
                                }
                            }}
                            checked={deliveryType === 'dbi'}
                            color="primary"
                            disabled={deliveryType === 'carrier'}
                            value="dbi"
                        />
                    }
                    label="DBI"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            id="delivery_type_carrier"
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setDeliveryType(event.target.value);
                                } else {
                                    setDeliveryType(null);
                                }
                            }}
                            checked={deliveryType === 'carrier'}
                            color="primary"
                            disabled={deliveryType === 'dbi'}
                            value="carrier"
                        />
                    }
                    label="Carrier"
                />
            </Grid>
        </div>
    );
}

function ShipmentStatus() {
    const [flowControlRows, setFlowControlRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [showDetailViewModalOpen, setShowDetailViewModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [orderSource, setOrderSource] = useState([]);
    const [deliveryType, setDeliveryType] = useState(null);
    const [startDate, setStartDate] = useState(startOfWeekDate);
    const [endDate, setEndDate] = useState(endOfWeekDate);
    const [loading, setLoading] = useState(false);
    const [filtersSelected, setFiltersSelected] = useState(false);

    // OOB Manifest modal state
    const [oobManifestModalOpen, setOobManifestModalOpen] = useState(false);
    const [oobManifestData, setOobManifestData] = useState(null);
    const [oobManifestLoading, setOobManifestLoading] = useState(new Set()); // Track loading by lineHaulID

    // Frozen Manifest modal state
    const [frozenManifestModalOpen, setFrozenManifestModalOpen] = useState(false);
    const [frozenManifestData, setFrozenManifestData] = useState(null);
    const [frozenManifestLoading, setFrozenManifestLoading] = useState(new Set()); // Track loading by lineHaulID

    const user = useContext(UserContext);

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

    useInterval(async () => {
        if (autoRefreshEnabled) getFlowControl();
    }, 15000);

    const showDetailViewModal = (row) => {
        setSelectedRow(row);
        setShowDetailViewModalOpen(true);
    };

    const handleOOBManifest = async (row) => {
        const lineHaulId = row.original.lineHaulID;

        // Set loading for this specific lineHaulID
        setOobManifestLoading((prev) => new Set(prev).add(lineHaulId));

        try {
            // Extract just the date portion (remove day name like "Monday ")
            const packDate = row.original.expectedPackDate.replace(/^[A-Za-z]+\s+/, '');

            if (!lineHaulId || !packDate) {
                setNotification({
                    text: `Missing required data - lineHaulId: ${lineHaulId}, packDate: ${packDate}`,
                    level: 'error',
                });
                return;
            }

            const response = await api.getOutOfBoxManifest(lineHaulId, packDate);

            if (response.status) {
                setNotification({ text: 'OOB Manifest retrieved successfully', level: 'success' });
                setOobManifestData(response.data);
                setOobManifestModalOpen(true);
            } else {
                setNotification({ text: `Failed to retrieve OOB Manifest: ${response.msg}`, level: 'error' });
            }
        } catch (error) {
            setNotification({ text: `Error retrieving OOB Manifest: ${error.message}`, level: 'error' });
        } finally {
            // Remove loading for this specific lineHaulID
            setOobManifestLoading((prev) => {
                const newSet = new Set(prev);
                newSet.delete(lineHaulId);
                return newSet;
            });
        }
    };

    const handleFrozenManifest = async (row) => {
        const lineHaulId = row.original.lineHaulID;

        // Set loading for this specific lineHaulID
        setFrozenManifestLoading((prev) => new Set(prev).add(lineHaulId));

        try {
            // Extract just the date portion (remove day name like "Monday ")
            const packDate = row.original.expectedPackDate.replace(/^[A-Za-z]+\s+/, '');

            if (!lineHaulId || !packDate) {
                setNotification({
                    text: `Missing required data - lineHaulId: ${lineHaulId}, packDate: ${packDate}`,
                    level: 'error',
                });
                return;
            }

            const response = await api.getFrozenManifest(lineHaulId, packDate);

            if (response.status) {
                setNotification({ text: 'Frozen Manifest retrieved successfully', level: 'success' });
                setFrozenManifestData(response.data);
                setFrozenManifestModalOpen(true);
            } else {
                setNotification({ text: `Failed to retrieve Frozen Manifest: ${response.msg}`, level: 'error' });
            }
        } catch (error) {
            setNotification({ text: `Error retrieving Frozen Manifest: ${error.message}`, level: 'error' });
        } finally {
            // Remove loading for this specific lineHaulID
            setFrozenManifestLoading((prev) => {
                const newSet = new Set(prev);
                newSet.delete(lineHaulId);
                return newSet;
            });
        }
    };

    // Load flow control table
    let getFlowControl = async () => {
        const response = await api.getShipmentStatusFlowControl(user.getFacilityID());
        if (response.status === false) {
            setAutoRefreshEnabled(false);
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setFlowControlRows([]);
            return;
        }

        if (!response.data.rows || !Array.isArray(response.data.rows)) {
            console.error('getShipmentStatusFlowControl response rows is not an array', response.data);
            setFlowControlRows([]);
            return;
        }

        setFlowControlRows(
            response.data.rows.map((row) => {
                // Store the original lineHaul as lineHaulId for the API call
                row.lineHaulId = row.lineHaul;
                if (row.packDayOffsetFrom) {
                    row.lineHaul = `${row.lineHaul} - offset from ${row.packDayOffsetFrom}`;
                }
                return row;
            }),
        );
    };

    // Load flow control table with filters
    const getFlowControlWithFilters = async () => {
        setAutoRefreshEnabled(false);
        setLoading(true);
        setFlowControlRows([]);

        const sendParams = {};
        sendParams.orderSource = orderSource;
        sendParams.deliveryType = deliveryType !== null ? [deliveryType] : [];

        const response = await api.getShipmentStatusFlowControlWithFilters(
            user.getFacilityID(),
            startDate,
            endDate,
            sendParams,
        );
        if (response.status === false) {
            setLoading(false);
            setAutoRefreshEnabled(false);
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setFlowControlRows([]);
            return;
        }

        if (!response.data.rows || !Array.isArray(response.data.rows)) {
            setLoading(false);
            console.error('getShipmentStatusFlowControl response rows is not an array', response.data);
            setFlowControlRows([]);
            return;
        }

        setFlowControlRows(
            response.data.rows.map((row) => {
                // Store the original lineHaul as lineHaulId for the API call
                row.lineHaulId = row.lineHaul;
                if (row.packDayOffsetFrom) {
                    row.lineHaul = `${row.lineHaul} - offset from ${row.packDayOffsetFrom}`;
                }
                return row;
            }),
        );
        setLoading(false);
    };

    const handleAutoRefresh = () => {
        setAutoRefreshEnabled(true);
        setOrderSource([]);
        setDeliveryType(null);
        setStartDate(startOfWeekDate);
        setEndDate(endOfWeekDate);
        setLoading(false);
        getFlowControl();
        setFiltersSelected(false);
    };
    useEffect(() => {
        getFlowControl();
    }, []);

    useEffect(() => {
        if (
            startDate !== startOfWeekDate ||
            endDate !== endOfWeekDate ||
            orderSource.length > 0 ||
            deliveryType !== null
        ) {
            setFiltersSelected(true);
        }

        if (
            startDate === startOfWeekDate &&
            endDate === endOfWeekDate &&
            orderSource.length === 0 &&
            deliveryType === null
        ) {
            setFiltersSelected(false);
        }
    }, [orderSource, deliveryType, startDate, endDate]);

    const flowControlColumns = [
        {
            accessor: '',
            Header: 'View Details',
            Cell: ({ cell: { value: initialValue }, row }) => (
                <LinkToDetailView initialValue={initialValue} row={row} showDetailViewModal={showDetailViewModal} />
            ),
        },
        { accessor: 'lineHaul', Header: 'Line Haul' },
        { accessor: 'companySource', Header: 'Company Source' },
        { accessor: 'marketCode', Header: 'Market Code' },
        { accessor: 'expectedPackDate', Header: 'Expected Pack Date' },
        { accessor: 'expectedPullTime', Header: 'Expected Ship Time' },
        { accessor: 'containers.PRE_PURCHASE.length', Header: 'Pre Purchase' },
        { accessor: 'containers.LABEL_PURCHASED.length', Header: 'Label Purchased' },
        { accessor: 'containers.LABEL_PRINTED.length', Header: 'Label Printed' },
        { accessor: 'containers.PACKED.length', Header: 'Packed' },
        { accessor: 'containers.ON_SHIPPING_PALLET.length', Header: 'On Shipping Pallet' },
        { accessor: 'containers.ON_TRUCK.length', Header: 'On Truck' },
        { accessor: 'containers.SHIPPED.length', Header: 'Shipped' },
        { accessor: 'containers.IN_TRANSIT.length', Header: 'In Transit' },
        { accessor: 'containers.DELIVERED.length', Header: 'Delivered' },
        { accessor: 'containers.ISSUE.length', Header: 'Issue' },
        { accessor: 'containers.CANCELLED.length', Header: 'Cancelled' },
        { accessor: 'containers.total.length', Header: 'Total' },
        {
            accessor: '',
            Header: 'OOB Manifest',
            Cell: ({ row }) => {
                const isLoading = oobManifestLoading.has(row.original.lineHaulID);
                return <OOBManifestButton row={row} onOOBManifestClick={handleOOBManifest} loading={isLoading} />;
            },
        },
        {
            accessor: '',
            Header: 'Frozen Manifest',
            Cell: ({ row }) => {
                const isLoading = frozenManifestLoading.has(row.original.lineHaulID);
                return (
                    <FrozenManifestButton row={row} onFrozenManifestClick={handleFrozenManifest} loading={isLoading} />
                );
            },
        },
    ];

    return (
        <div>
            <h1>Shipment Status Report</h1>

            {loading === true && <h3>⚠️ Please wait while the data is loading... ⚠️ </h3>}

            {autoRefreshEnabled == true && <p>Note: This page refreshes automatically every 15 seconds.</p>}

            {autoRefreshEnabled != true && (
                <h4>
                    ⚠️ Auto refresh as been disabled, please refresh your page manually. ⚠️
                    <br />
                    ⚠️ If you have applied filters, please clear filters to refresh the page. ⚠️
                </h4>
            )}

            <Notifications options={notification} />

            {/* Modals - placed outside table to avoid re-renders */}
            {showDetailViewModalOpen && (
                <ShipmentStatusDetailView
                    rowData={selectedRow}
                    closeModal={() => {
                        setShowDetailViewModalOpen(false);
                    }}
                />
            )}

            <OOBManifestModal
                open={oobManifestModalOpen}
                onOpenChange={(isOpen) => {
                    setOobManifestModalOpen(isOpen);
                    if (!isOpen) {
                        setOobManifestData(null);
                    }
                }}
                manifestData={oobManifestData}
                setNotification={setNotification}
            />

            <FrozenManifestModal
                open={frozenManifestModalOpen}
                onOpenChange={(isOpen) => {
                    setFrozenManifestModalOpen(isOpen);
                    if (!isOpen) {
                        setFrozenManifestData(null);
                    }
                }}
                manifestData={frozenManifestData}
                setNotification={setNotification}
            />

            <div>
                <h3>Flow Control</h3>
                <FilterOptions
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    orderSource={orderSource}
                    setOrderSource={setOrderSource}
                    deliveryType={deliveryType}
                    setDeliveryType={setDeliveryType}
                />
                <div style={{ marginBottom: 10 }}>
                    <Button onClick={getFlowControlWithFilters} color="secondary" disabled={!filtersSelected}>
                        Apply Filters
                    </Button>
                    <Button onClick={handleAutoRefresh} color="secondary">
                        Clear Filters
                    </Button>
                </div>
                <DataTable
                    columns={flowControlColumns}
                    data={flowControlRows}
                    csvExport
                    csvExportFileName="ops-visibility"
                    csvExportHeadersUseAccessor
                />
            </div>
        </div>
    );
}

export { ShipmentStatus };
